/**
 * Connected accounts — Settings → Security. The Clerk work is
 * `useConnectedAccounts` (@tonnie/sdk); this is the screen's side: the words
 * for each outcome (never Clerk's English), a report for anything unexpected,
 * and which connection is being removed.
 */
import { SOCIAL_PROVIDER_NAME, type SocialProvider } from '@tonnie/foundation';
import { useAuthErrorMessage, useTranslation } from '@tonnie/locale';
import { useErrorCapture } from '@tonnie/observability';
import { useConnectedAccounts, type ConnectedAccount } from '@tonnie/sdk';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useAccountPortal } from '../../portal';

export function useConnectedAccountsCard() {
  const { t } = useTranslation();
  // Back here after the provider's consent screen.
  const { securitySettingsPath } = useAccountPortal();
  const describeAuthError = useAuthErrorMessage();
  const { captureException } = useErrorCapture();
  const { accounts, available, connect, disconnect, returned, clearReturned } =
    useConnectedAccounts();
  const [busy, setBusy] = useState<SocialProvider | null>(null);
  const [removing, setRemoving] = useState<ConnectedAccount | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fail = useCallback(
    (err: unknown, fallback: string, action: string) => {
      const failure = describeAuthError(err, fallback);
      if (!failure.expected) {
        captureException(err instanceof Error ? err : new Error(String(err)), {
          component: 'connected-accounts',
          action,
        });
      }
      setError(failure.message);
    },
    [describeAuthError, captureException]
  );

  // Back from the provider: say how it went.
  useEffect(() => {
    if (!returned) return;
    const provider = SOCIAL_PROVIDER_NAME[returned.provider];
    if (returned.result === 'connected') {
      toast.success(t('auth.connected.connected', { provider }));
    } else {
      fail(returned.error, t('auth.connected.failed', { provider }), 'return');
    }
    clearReturned();
  }, [returned, clearReturned, fail, t]);

  const onConnect = useCallback(
    async (provider: SocialProvider) => {
      setError(null);
      setBusy(provider);
      const result = await connect(provider, securitySettingsPath);
      // `done` means the provider's consent screen is opening — stay busy.
      if (result.outcome !== 'done') setBusy(null);
      if (result.outcome === 'failed') {
        fail(
          result.error,
          t('auth.connected.failed', { provider: SOCIAL_PROVIDER_NAME[provider] }),
          'connect'
        );
      }
    },
    [connect, securitySettingsPath, fail, t]
  );

  const onConfirmDisconnect = useCallback(async () => {
    if (!removing) return;
    const provider = SOCIAL_PROVIDER_NAME[removing.provider];
    setError(null);
    setBusy(removing.provider);
    const result = await disconnect(removing.id);
    setBusy(null);
    setRemoving(null);
    switch (result.outcome) {
      case 'done':
        toast.success(t('auth.connected.disconnected', { provider }));
        return;
      case 'cancelled':
        return;
      case 'failed':
        fail(result.error, t('auth.connected.disconnectFailed', { provider }), 'disconnect');
        return;
      default: {
        const exhaustive: never = result;
        throw new Error(`Unhandled outcome: ${JSON.stringify(exhaustive)}`);
      }
    }
  }, [removing, disconnect, fail, t]);

  return {
    accounts,
    available,
    busy,
    error,
    removing,
    askToDisconnect: setRemoving,
    cancelDisconnect: useCallback(() => setRemoving(null), []),
    onConnect,
    onConfirmDisconnect,
  };
}
